import { useModalLifecycle } from '../shared/useModalLifecycle'
/**
 * Team Spain v0 — Modal de Seguimiento Semanal de Atleta (M6)
 * 
 * Implementa el formulario semanal de docs/FORMS_V0.md:
 * - Periodo lunes-domingo explícito en la zona horaria del equipo.
 * - Resumen de entrenamientos ya registrados (sin doble introducción).
 * - Calidad de sueño (0-24h, calidad 1-5, sensaciones al despertar).
 * - Bienestar (fatiga 1-5, dolor muscular 1-5, recuperación 1-5, estrés 1-5, ánimo 1-5).
 * - Molestias/lesiones condicionales (zonas, lados, dolor 0-10, vinculación a episodio).
 * - Enfermedad y atención profesional condicionales.
 * - Disponibilidad declarada y solicitud de contacto.
 * - Guardado de borrador online vs Envío definitivo auditado.
 */

import React, { useState, useEffect } from 'react'
import {
  X,
  ClipboardList,
  Moon,
  HeartPulse,
  Activity,
  Save,
  Send,
  AlertCircle,
  CheckCircle2,
  Plus,
  ChevronRight,
  ChevronLeft,
  Utensils,
} from 'lucide-react'
import { formsService } from './formsService'
import {
  WeeklyFormPayload,
  DiscomfortEntry,
  TrainingLogItem,
  HealthIncidentItem,
  BodyZone,
  Laterality,
  DeclaredAvailability,
  ActivityEffect,
  NutritionQuality,
  WaterIntake,
  ProteinFrequency,
  FruitVegServings,
  PostWorkoutIntake,
  NUTRITION_QUALITY_LABELS,
  WATER_INTAKE_LABELS,
  PROTEIN_FREQUENCY_LABELS,
  FRUIT_VEG_SERVINGS_LABELS,
  POST_WORKOUT_INTAKE_LABELS,
} from '../../types/health'
import { BODY_ZONES_CONFIG } from './IncidentReportModal'

interface WeeklyFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (status: 'draft' | 'submitted') => void
  teamId: string
  athleteId: string
  periodStart: string // YYYY-MM-DD (lunes)
  periodEnd: string   // YYYY-MM-DD (domingo)
  initialPayload?: WeeklyFormPayload | null
  openEpisodes?: HealthIncidentItem[]
  onOpenTrainingModal?: () => void
  versionId?: string
}

const WAKE_FEELINGS_OPTIONS = [
  { id: 'somnolencia', label: 'Somnolencia excesiva' },
  { id: 'falta_energia', label: 'Falta de energía' },
  { id: 'no_descanso', label: 'Sensación de no haber descansado' },
  { id: 'mal_humor', label: 'Mal humor / Irritabilidad' },
  { id: 'dolor_cabeza', label: 'Dolor de cabeza' },
  { id: 'apatia', label: 'Apatía o desgana' },
  { id: 'none', label: 'Ninguna (despertar fresco y descansado)' },
]

export const WeeklyFormModal: React.FC<WeeklyFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  teamId,
  athleteId,
  periodStart,
  periodEnd,
  initialPayload,
  openEpisodes = [],
  onOpenTrainingModal,
  versionId,
}) => {
  const isV1 = versionId === 'weekly_v1'
  const [currentStep, setCurrentStep] = useState<number>(1)
  const [trainingLogs, setTrainingLogs] = useState<TrainingLogItem[]>([])
  const [isLoadingLogs, setIsLoadingLogs] = useState<boolean>(true)

  // Estados del formulario
  const [fatigue, setFatigue] = useState<number>(initialPayload?.fatigue ?? 2)
  const [muscleSoreness, setMuscleSoreness] = useState<number>(initialPayload?.muscle_soreness ?? 2)
  const [recovery, setRecovery] = useState<number>(initialPayload?.recovery ?? 4)
  const [stress, setStress] = useState<number>(initialPayload?.stress ?? 2)
  const [mood, setMood] = useState<number>(initialPayload?.mood ?? 4)
  const [sleepHours, setSleepHours] = useState<number>(initialPayload?.sleep_hours ?? 7.5)
  const [sleepQuality, setSleepQuality] = useState<number>(initialPayload?.sleep_quality ?? 4)
  const [wakeFeelings, setWakeFeelings] = useState<string[]>(
    initialPayload?.wake_feelings || ['none']
  )

  // Molestias
  const [hasDiscomfort, setHasDiscomfort] = useState<boolean>(
    initialPayload?.has_discomfort ?? false
  )
  const [discomfortAreas, setDiscomfortAreas] = useState<DiscomfortEntry[]>(
    initialPayload?.discomfort_areas && initialPayload.discomfort_areas.length > 0
      ? initialPayload.discomfort_areas
      : [
          {
            zone: 'thigh_hamstring',
            side: 'right',
            pain_level: 4,
            is_new: true,
          },
        ]
  )
  const [activityImpact, setActivityImpact] = useState<ActivityEffect>(
    initialPayload?.activity_impact || 'none'
  )
  const [impactExplanation, setImpactExplanation] = useState<string>(
    initialPayload?.impact_explanation || ''
  )
  const [performanceImpact, setPerformanceImpact] = useState<number>(
    initialPayload?.performance_impact ?? 1
  )

  // Enfermedad
  const [hasIllness, setHasIllness] = useState<boolean>(initialPayload?.has_illness ?? false)
  const [illnessDescription, setIllnessDescription] = useState<string>(
    initialPayload?.illness_description || ''
  )
  const [illnessDaysMissed, setIllnessDaysMissed] = useState<number>(
    initialPayload?.illness_days_missed ?? 0
  )

  // Atención profesional
  const [hasProfessionalCare, setHasProfessionalCare] = useState<boolean>(
    initialPayload?.has_professional_care ?? false
  )
  const [careType, setCareType] = useState<string>(initialPayload?.professional_care_type || '')
  const [careReason, setCareReason] = useState<string>(initialPayload?.professional_care_reason || '')
  const [careLimitation, setCareLimitation] = useState<string>(
    initialPayload?.professional_care_limitation || ''
  )

  // Disponibilidad y contacto
  const [declaredAvailability, setDeclaredAvailability] = useState<DeclaredAvailability>(
    initialPayload?.declared_availability || 'available_100'
  )
  const [contactRequested, setContactRequested] = useState<boolean>(
    initialPayload?.contact_requested ?? false
  )
  const [contactComment, setContactComment] = useState<string>(
    initialPayload?.contact_comment || ''
  )

  // Estados de Alimentación e Hidratación (v2)
  const [nutritionQuality, setNutritionQuality] = useState<NutritionQuality | undefined>(
    initialPayload?.nutrition_quality
  )
  const [waterIntake, setWaterIntake] = useState<WaterIntake | undefined>(
    initialPayload?.water_intake
  )
  const [proteinFrequency, setProteinFrequency] = useState<ProteinFrequency | undefined>(
    initialPayload?.protein_frequency
  )
  const [fruitVegServings, setFruitVegServings] = useState<FruitVegServings | undefined>(
    initialPayload?.fruit_veg_servings
  )
  const [postWorkoutIntake, setPostWorkoutIntake] = useState<PostWorkoutIntake | undefined>(
    initialPayload?.post_workout_protein_carb
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const [draftSavedToast, setDraftSavedToast] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [confirmSubmitModal, setConfirmSubmitModal] = useState(false)

  // Sincronizar estado cuando initialPayload cambie (ej. al abrir borrador existente)
  useEffect(() => {
    if (initialPayload) {
      if (initialPayload.nutrition_quality !== undefined) setNutritionQuality(initialPayload.nutrition_quality)
      if (initialPayload.water_intake !== undefined) setWaterIntake(initialPayload.water_intake)
      if (initialPayload.protein_frequency !== undefined) setProteinFrequency(initialPayload.protein_frequency)
      if (initialPayload.fruit_veg_servings !== undefined) setFruitVegServings(initialPayload.fruit_veg_servings)
      if (initialPayload.post_workout_protein_carb !== undefined) setPostWorkoutIntake(initialPayload.post_workout_protein_carb)
    }
  }, [initialPayload])

  // Cargar entrenamientos registrados durante esta semana concreta
  useEffect(() => {
    if (!isOpen) return
    setIsLoadingLogs(true)
    formsService
      .getTrainingLogs(teamId, athleteId, periodStart, periodEnd)
      .then((logs) => setTrainingLogs(logs))
      .catch((err) => console.error('Error cargando entrenamientos de la semana:', err))
      .finally(() => setIsLoadingLogs(false))
  }, [isOpen, teamId, athleteId, periodStart, periodEnd])

  const isDirty =
    !draftSavedToast &&
    (fatigue !== (initialPayload?.fatigue ?? 2) ||
      muscleSoreness !== (initialPayload?.muscle_soreness ?? 2) ||
      recovery !== (initialPayload?.recovery ?? 4) ||
      stress !== (initialPayload?.stress ?? 2) ||
      mood !== (initialPayload?.mood ?? 4) ||
      sleepHours !== (initialPayload?.sleep_hours ?? 7.5) ||
      hasDiscomfort !== (initialPayload?.has_discomfort ?? false) ||
      hasIllness !== (initialPayload?.has_illness ?? false) ||
      hasProfessionalCare !== (initialPayload?.has_professional_care ?? false) ||
      declaredAvailability !== (initialPayload?.declared_availability || 'available_100') ||
      contactRequested !== (initialPayload?.contact_requested ?? false) ||
      contactComment.trim() !== (initialPayload?.contact_comment || ''))

  const handleCloseAttempt = () => {
    if (isDirty) {
      const confirmLeave = window.confirm(
        'Tienes respuestas modificadas en el formulario semanal sin guardar. ¿Deseas descartarlas y cerrar?'
      )
      if (!confirmLeave) return
    }
    onClose()
  }

  useModalLifecycle(isOpen, () => {
    if (confirmSubmitModal) setConfirmSubmitModal(false)
    else handleCloseAttempt()
  })

  if (!isOpen) return null

  // Manejador de sensaciones al despertar (ninguna es excluyente)
  const handleToggleWakeFeeling = (id: string) => {
    if (id === 'none') {
      setWakeFeelings(['none'])
    } else {
      const filtered = wakeFeelings.filter((f) => f !== 'none')
      if (filtered.includes(id)) {
        const next = filtered.filter((f) => f !== id)
        setWakeFeelings(next.length === 0 ? ['none'] : next)
      } else {
        setWakeFeelings([...filtered, id])
      }
    }
  }

  // Añadir una zona de dolor adicional
  const handleAddDiscomfortArea = () => {
    setDiscomfortAreas([
      ...discomfortAreas,
      {
        zone: 'knee',
        side: 'right',
        pain_level: 3,
        is_new: true,
      },
    ])
  }

  const handleRemoveDiscomfortArea = (index: number) => {
    setDiscomfortAreas(discomfortAreas.filter((_, idx) => idx !== index))
  }

  const handleUpdateDiscomfortArea = (index: number, patch: Partial<DiscomfortEntry>) => {
    const updated = [...discomfortAreas]
    updated[index] = { ...updated[index], ...patch }
    setDiscomfortAreas(updated)
  }

  // Agrupar carga total de la semana registrada
  const totalWeekMinutes = trainingLogs.reduce((acc, l) => acc + l.duration_minutes, 0)
  const totalWeekLoad = Math.round(
    trainingLogs.reduce((acc, l) => acc + Number(l.session_load || l.duration_minutes * l.rpe), 0)
  )

  const buildPayload = (): WeeklyFormPayload => ({
    fatigue,
    muscle_soreness: muscleSoreness,
    recovery,
    stress,
    mood,
    sleep_hours: Number(sleepHours),
    sleep_quality: sleepQuality,
    wake_feelings: wakeFeelings,
    has_discomfort: hasDiscomfort,
    discomfort_areas: hasDiscomfort ? discomfortAreas : undefined,
    activity_impact: hasDiscomfort ? activityImpact : undefined,
    impact_explanation: hasDiscomfort ? impactExplanation : undefined,
    performance_impact: hasDiscomfort ? performanceImpact : undefined,
    has_illness: hasIllness,
    illness_description: hasIllness ? illnessDescription : undefined,
    illness_days_missed: hasIllness ? illnessDaysMissed : undefined,
    has_professional_care: hasProfessionalCare,
    professional_care_type: hasProfessionalCare ? careType : undefined,
    professional_care_reason: hasProfessionalCare ? careReason : undefined,
    professional_care_limitation: hasProfessionalCare ? careLimitation : undefined,
    declared_availability: declaredAvailability,
    contact_requested: contactRequested,
    contact_comment: contactRequested ? contactComment : undefined,

    // Bloque v2: Alimentación e Hidratación
    nutrition_quality: nutritionQuality,
    water_intake: waterIntake,
    protein_frequency: proteinFrequency,
    fruit_veg_servings: fruitVegServings,
    post_workout_protein_carb: postWorkoutIntake,
  })

  // Validación antes de abrir confirmación de envío definitivo
  const handleOpenConfirmSubmit = () => {
    setErrorMsg(null)
    // En weekly_v1, no se exige responder nutrición al editar el envío conforme a su versión original
    if (!isV1) {
      if (
        nutritionQuality === undefined ||
        !waterIntake ||
        !proteinFrequency ||
        !fruitVegServings ||
        !postWorkoutIntake
      ) {
        setErrorMsg('Debes responder a las 5 preguntas del bloque de Alimentación e Hidratación antes de enviar.')
        setCurrentStep(4)
        return
      }
    }
    setConfirmSubmitModal(true)
  }

  // Guardar borrador online
  const handleSaveDraft = async () => {
    setErrorMsg(null)
    setIsSavingDraft(true)
    try {
      const payload = buildPayload()
      await formsService.saveWeeklyFormResponse(
        teamId,
        athleteId,
        periodStart,
        periodEnd,
        payload,
        true // isDraft = true
      )
      setDraftSavedToast(true)
      setTimeout(() => setDraftSavedToast(false), 3000)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al guardar el borrador.'
      setErrorMsg(msg)
    } finally {
      setIsSavingDraft(false)
    }
  }

  // Enviar definitivamente
  const handleFinalSubmit = async () => {
    setErrorMsg(null)
    setIsSubmitting(true)
    try {
      const payload = buildPayload()
      await formsService.saveWeeklyFormResponse(
        teamId,
        athleteId,
        periodStart,
        periodEnd,
        payload,
        false // isDraft = false
      )
      setConfirmSubmitModal(false)
      onSuccess('submitted')
      onClose()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al enviar el formulario.'
      setErrorMsg(msg)
      setConfirmSubmitModal(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="mobile-viewport-overlay health-form-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCloseAttempt()
      }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(10, 19, 32, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300,
        padding: '0.75rem',
      }}
    >
      <div
        className="modal-sheet-mobile mobile-viewport-sheet"
        style={{
          backgroundColor: 'var(--surface-card)',
          borderRadius: '12px',
          padding: '1.25rem',
          maxWidth: '580px',
          width: '100%',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-card)',
          border: '1px solid var(--surface-border)',
        }}
      >
        {/* Cabecera del formulario */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.85rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <ClipboardList size={18} style={{ color: 'var(--brand-red)' }} />
              <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>
                Seguimiento Semanal
              </h2>
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
              Periodo: <strong>{periodStart}</strong> al <strong>{periodEnd}</strong> (Zona Europe/Madrid)
            </div>
          </div>
          <button
            type="button"
            onClick={handleCloseAttempt}
            aria-label="Cerrar modal"
            style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Notificación de borrador guardado */}
        {draftSavedToast && (
          <div
            style={{
              padding: '0.5rem 0.75rem',
              backgroundColor: 'var(--status-success-bg)',
              color: 'var(--status-success-text)',
              borderRadius: '6px',
              fontSize: '0.8rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.75rem',
            }}
          >
            <CheckCircle2 size={16} />
            <span>Borrador guardado online correctamente. Puedes salir y continuar cuando desees.</span>
          </div>
        )}

        {errorMsg && (
          <div
            style={{
              padding: '0.65rem 0.75rem',
              backgroundColor: 'var(--status-danger-bg)',
              color: 'var(--status-danger-text)',
              borderRadius: '6px',
              fontSize: '0.8rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.75rem',
            }}
          >
            <AlertCircle size={16} />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Indicador de pasos */}
        <div
          className="step-pill-grid"
          style={{ display: 'flex', gap: '0.3rem', marginBottom: '1rem', flexWrap: 'wrap' }}
        >
          {[
            { num: 1, label: '1. Entrenamientos' },
            { num: 2, label: '2. Sueño' },
            { num: 3, label: '3. Bienestar' },
            { num: 4, label: '4. Nutrición' },
            { num: 5, label: '5. Salud y Disp.' },
          ].map((st) => (
            <button
              key={st.num}
              type="button"
              onClick={() => setCurrentStep(st.num)}
              style={{
                flex: '1 1 0',
                minHeight: '38px',
                padding: '0.4rem 0.2rem',
                fontSize: '0.72rem',
                fontWeight: currentStep === st.num ? 700 : 500,
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                backgroundColor: currentStep === st.num ? 'var(--brand-red)' : 'var(--surface-sunken)',
                color: currentStep === st.num ? '#ffffff' : 'var(--text-secondary)',
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                whiteSpace: 'nowrap',
              }}
            >
              {st.label}
            </button>
          ))}
        </div>

        {/* CUERPO DEL PASO ACTIVO */}
        <div className="mobile-form-body" style={{ flex: 1, overflowY: 'auto', paddingRight: '0.25rem', marginBottom: '1rem' }}>
          {/* PASO 1: ENTRENAMIENTOS DE LA SEMANA */}
          {currentStep === 1 && (
            <div>
              <div style={{ marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '0.92rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                  Resumen de entrenamientos realizados
                </h3>
                <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: 0 }}>
                  Sesiones que ya has registrado para esta semana. No hace falta que las vuelvas a introducir.
                </p>
              </div>

              {isLoadingLogs ? (
                <div style={{ padding: '1.5rem', textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  Consultando entrenamientos de la semana...
                </div>
              ) : trainingLogs.length === 0 ? (
                <div
                  style={{
                    padding: '1.25rem',
                    backgroundColor: 'var(--surface-sunken)',
                    borderRadius: '8px',
                    textAlign: 'center',
                    border: '1px dashed var(--surface-border)',
                    marginBottom: '1rem',
                  }}
                >
                  <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                    No tienes entrenamientos registrados en este periodo ({periodStart} al {periodEnd}).
                  </p>
                  {onOpenTrainingModal && (
                    <button
                      type="button"
                      onClick={onOpenTrainingModal}
                      className="btn btn-secondary"
                      style={{ fontSize: '0.75rem' }}
                    >
                      <Plus size={14} style={{ marginRight: '0.3rem' }} />
                      Registrar entrenamiento de esta semana
                    </button>
                  )}
                </div>
              ) : (
                <div style={{ marginBottom: '1rem' }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                      gap: '0.5rem',
                      marginBottom: '0.75rem',
                    }}
                  >
                    <div className="card" style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>Sesiones</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 800 }}>{trainingLogs.length}</div>
                    </div>
                    <div className="card" style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>Minutos totales</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 800 }}>{totalWeekMinutes}m</div>
                    </div>
                    <div className="card" style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>Carga sRPE</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--brand-red)' }}>
                        {totalWeekLoad} UA
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {trainingLogs.map((log) => (
                      <div
                        key={log.id}
                        style={{
                          padding: '0.5rem 0.75rem',
                          backgroundColor: 'var(--surface-sunken)',
                          borderRadius: '6px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: '0.78rem',
                        }}
                      >
                        <div>
                          <strong>{log.session_date}</strong> · <span style={{ textTransform: 'capitalize' }}>{log.activity_type}</span>
                          {log.other_activity_description && ` (${log.other_activity_description})`}
                          <span style={{ color: 'var(--text-secondary)', marginLeft: '0.4rem' }}>
                            ({log.origin === 'selection' ? 'Selección' : log.origin === 'club' ? 'Club' : 'Propio'})
                          </span>
                        </div>
                        <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {log.duration_minutes}m · RPE {log.rpe} · {Math.round(log.session_load || log.duration_minutes * log.rpe)} UA
                        </div>
                      </div>
                    ))}
                  </div>

                  {onOpenTrainingModal && (
                    <div style={{ marginTop: '0.75rem', textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={onOpenTrainingModal}
                        className="btn btn-secondary"
                        style={{ fontSize: '0.72rem', padding: '0.25rem 0.5rem' }}
                      >
                        <Plus size={13} style={{ marginRight: '0.25rem' }} />
                        Añadir otra sesión a esta semana
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* PASO 2: SUEÑO Y RECUPERACIÓN */}
          {currentStep === 2 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '1rem' }}>
                <Moon size={16} style={{ color: '#818cf8' }} />
                <h3 style={{ fontSize: '0.92rem', fontWeight: 700, margin: 0 }}>
                  Horas de sueño y calidad del descanso
                </h3>
              </div>

              {/* Horas medias de sueño */}
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>
                    Horas medias de sueño diario esta semana (0 a 24h) *
                  </label>
                  <span style={{ fontSize: '0.9rem', fontWeight: 800, fontFamily: 'monospace' }}>
                    {sleepHours} horas
                  </span>
                </div>
                <input
                  type="range"
                  min="4"
                  max="12"
                  step="0.5"
                  value={sleepHours}
                  onChange={(e) => setSleepHours(Number(e.target.value))}
                  style={{ width: '100%', accentColor: '#818cf8' }}
                />
                <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.35rem' }}>
                  {[6, 7, 7.5, 8, 8.5, 9].map((h) => (
                    <button
                      key={h}
                      type="button"
                      onClick={() => setSleepHours(h)}
                      className={`btn ${sleepHours === h ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem', flex: 1 }}
                    >
                      {h}h
                    </button>
                  ))}
                </div>
              </div>

              {/* Calidad de sueño (1-5) */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.4rem' }}>
                  Calidad general del sueño *
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.35rem' }}>
                  {[
                    { val: 1, label: '1. Muy mala' },
                    { val: 2, label: '2. Mala' },
                    { val: 3, label: '3. Regular' },
                    { val: 4, label: '4. Buena' },
                    { val: 5, label: '5. Muy buena' },
                  ].map((opt) => (
                    <button
                      key={opt.val}
                      type="button"
                      onClick={() => setSleepQuality(opt.val)}
                      className={`btn ${sleepQuality === opt.val ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ fontSize: '0.7rem', padding: '0.45rem 0.2rem', textAlign: 'center' }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sensaciones al despertar */}
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.4rem' }}>
                  Sensaciones al despertar (selecciona las que apliquen):
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {WAKE_FEELINGS_OPTIONS.map((wf) => {
                    const isChecked = wakeFeelings.includes(wf.id)
                    return (
                      <label
                        key={wf.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.45rem 0.6rem',
                          backgroundColor: isChecked ? 'rgba(59, 130, 246, 0.08)' : 'var(--surface-sunken)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.78rem',
                          border: isChecked ? '1px solid var(--brand-blue, #3b82f6)' : '1px solid transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggleWakeFeeling(wf.id)}
                        />
                        <span>{wf.label}</span>
                      </label>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          {/* PASO 3: BIENESTAR, FATIGA Y ESTRÉS */}
          {currentStep === 3 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '1rem' }}>
                <HeartPulse size={16} style={{ color: 'var(--brand-red)' }} />
                <h3 style={{ fontSize: '0.92rem', fontWeight: 700, margin: 0 }}>
                  Percepción de recuperación, fatiga y estrés
                </h3>
              </div>

              {/* Recuperación (1-5) */}
              <div style={{ marginBottom: '1.1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Recuperación general *</label>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                    {['', '1. Muy mala', '2. Mala', '3. Aceptable', '4. Buena', '5. Muy buena'][recovery]}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.3rem' }}>
                  {[1, 2, 3, 4, 5].map((val) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setRecovery(val)}
                      className={`btn ${recovery === val ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ fontSize: '0.75rem', padding: '0.4rem 0.2rem' }}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Fatiga General (1-5) */}
              <div style={{ marginBottom: '1.1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Fatiga general acumulada *</label>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                    {['', '1. Nada fatigado', '2. Leve', '3. Moderada', '4. Alta', '5. Fatiga extrema'][fatigue]}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.3rem' }}>
                  {[1, 2, 3, 4, 5].map((val) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setFatigue(val)}
                      className={`btn ${fatigue === val ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ fontSize: '0.75rem', padding: '0.4rem 0.2rem' }}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dolor Muscular / Agujetas (1-5) */}
              <div style={{ marginBottom: '1.1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Dolor muscular / Agujetas general *</label>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                    {['', '1. Ninguno', '2. Leve', '3. Moderado', '4. Alto', '5. Extremo'][muscleSoreness]}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.3rem' }}>
                  {[1, 2, 3, 4, 5].map((val) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setMuscleSoreness(val)}
                      className={`btn ${muscleSoreness === val ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ fontSize: '0.75rem', padding: '0.4rem 0.2rem' }}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Estrés General (1-5) */}
              <div style={{ marginBottom: '1.1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Nivel de estrés general (vital/deportivo) *</label>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                    {['', '1. Muy bajo', '2. Bajo', '3. Moderado', '4. Alto', '5. Muy alto'][stress]}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.3rem' }}>
                  {[1, 2, 3, 4, 5].map((val) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setStress(val)}
                      className={`btn ${stress === val ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ fontSize: '0.75rem', padding: '0.4rem 0.2rem' }}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Estado de Ánimo (1-5) */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Estado de ánimo / Motivación *</label>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                    {['', '1. Muy bajo', '2. Bajo', '3. Normal', '4. Bueno', '5. Excelente'][mood]}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.3rem' }}>
                  {[1, 2, 3, 4, 5].map((val) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setMood(val)}
                      className={`btn ${mood === val ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ fontSize: '0.75rem', padding: '0.4rem 0.2rem' }}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* PASO 4: ALIMENTACIÓN E HIDRATACIÓN (v2) */}
          {currentStep === 4 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.35rem' }}>
                <Utensils size={16} style={{ color: 'var(--brand-red)' }} />
                <h3 style={{ fontSize: '0.92rem', fontWeight: 700, margin: 0 }}>
                  Alimentación e Hidratación
                </h3>
              </div>
              <p style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', marginBottom: '1.15rem' }}>
                Todas las preguntas hacen referencia al mismo periodo semanal ({periodStart} al {periodEnd}).
              </p>

              {isV1 && (
                <div
                  style={{
                    padding: '0.65rem 0.85rem',
                    backgroundColor: 'var(--surface-subtle)',
                    border: '1px solid var(--surface-border)',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    fontSize: '0.78rem',
                    color: 'var(--text-secondary)',
                    lineHeight: 1.4,
                  }}
                >
                  ℹ️ <strong>Envío histórico (v1):</strong> Este formulario fue entregado bajo la versión original. Las preguntas de nutrición son opcionales para la corrección de este registro. Si no seleccionas respuestas, se mantendrá sin nutrición conforme a su esquema original.
                </div>
              )}

              {/* Pregunta 1 */}
              <div style={{ marginBottom: '1.25rem', backgroundColor: 'var(--surface-sunken)', padding: '0.75rem', borderRadius: '8px' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: '0.45rem' }}>
                  Del 1 al 5, ¿cómo valorarías tu alimentación esta semana? *
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.35rem' }}>
                  {([1, 2, 3, 4, 5] as NutritionQuality[]).map((val) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setNutritionQuality(val)}
                      className={`btn ${nutritionQuality === val ? 'btn-primary' : 'btn-secondary'}`}
                      style={{
                        minHeight: '44px',
                        padding: '0.4rem 0.2rem',
                        fontSize: '0.74rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1.2,
                      }}
                    >
                      <span style={{ fontWeight: 800, fontSize: '0.95rem' }}>{val}</span>
                      <span style={{ fontSize: '0.67rem', opacity: 0.9 }}>
                        {val === 1 ? 'Muy mala' : val === 2 ? 'Mala' : val === 3 ? 'Regular' : val === 4 ? 'Buena' : 'Muy buena'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Pregunta 2 */}
              <div style={{ marginBottom: '1.25rem', backgroundColor: 'var(--surface-sunken)', padding: '0.75rem', borderRadius: '8px' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: '0.45rem' }}>
                  De media, ¿cuánta agua has bebido al día esta semana, incluyendo los entrenamientos? *
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.4rem' }}>
                  {(['lt_1l', '1_to_2l', '2_to_3l', 'gt_3l'] as WaterIntake[]).map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setWaterIntake(code)}
                      className={`btn ${waterIntake === code ? 'btn-primary' : 'btn-secondary'}`}
                      style={{
                        minHeight: '44px',
                        padding: '0.45rem 0.5rem',
                        fontSize: '0.76rem',
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {WATER_INTAKE_LABELS[code]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Pregunta 3 */}
              <div style={{ marginBottom: '1.25rem', backgroundColor: 'var(--surface-sunken)', padding: '0.75rem', borderRadius: '8px' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: '0.15rem' }}>
                  ¿Con qué frecuencia has incluido una fuente de proteína en tus comidas principales? *
                </label>
                <p style={{ fontSize: '0.73rem', color: 'var(--text-secondary)', marginBottom: '0.45rem' }}>
                  Por ejemplo: carne, pescado, huevos, lácteos, legumbres o tofu.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.4rem' }}>
                  {(['none_or_rare', 'some', 'most', 'all_or_almost'] as ProteinFrequency[]).map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setProteinFrequency(code)}
                      className={`btn ${proteinFrequency === code ? 'btn-primary' : 'btn-secondary'}`}
                      style={{
                        minHeight: '44px',
                        padding: '0.45rem 0.5rem',
                        fontSize: '0.76rem',
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {PROTEIN_FREQUENCY_LABELS[code]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Pregunta 4 */}
              <div style={{ marginBottom: '1.25rem', backgroundColor: 'var(--surface-sunken)', padding: '0.75rem', borderRadius: '8px' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: '0.15rem' }}>
                  De media, ¿cuántas raciones de fruta y verdura has tomado al día? *
                </label>
                <p style={{ fontSize: '0.73rem', color: 'var(--text-secondary)', marginBottom: '0.45rem' }}>
                  Una pieza de fruta o una porción de verdura cuenta como una ración.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.4rem' }}>
                  {(['0_to_1', '2_to_3', '4_to_5', 'gt_5'] as FruitVegServings[]).map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setFruitVegServings(code)}
                      className={`btn ${fruitVegServings === code ? 'btn-primary' : 'btn-secondary'}`}
                      style={{
                        minHeight: '44px',
                        padding: '0.45rem 0.35rem',
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {FRUIT_VEG_SERVINGS_LABELS[code]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Pregunta 5 */}
              <div style={{ marginBottom: '0.5rem', backgroundColor: 'var(--surface-sunken)', padding: '0.75rem', borderRadius: '8px' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: '0.45rem' }}>
                  ¿Con qué frecuencia has tomado algo con carbohidratos y proteína después de entrenar o competir? *
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.4rem' }}>
                  {(['never_or_rare', 'sometimes', 'almost_always', 'always', 'not_trained'] as PostWorkoutIntake[]).map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setPostWorkoutIntake(code)}
                      className={`btn ${postWorkoutIntake === code ? 'btn-primary' : 'btn-secondary'}`}
                      style={{
                        minHeight: '44px',
                        padding: '0.45rem 0.5rem',
                        fontSize: '0.76rem',
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ...(code === 'not_trained' ? { fontStyle: 'italic' } : {}),
                      }}
                    >
                      {POST_WORKOUT_INTAKE_LABELS[code]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* PASO 5: SALUD, MOLESTIAS Y DISPONIBILIDAD */}
          {currentStep === 5 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '1rem' }}>
                <Activity size={16} style={{ color: 'var(--status-danger-text)' }} />
                <h3 style={{ fontSize: '0.92rem', fontWeight: 700, margin: 0 }}>
                  Molestias, salud y disponibilidad
                </h3>
              </div>

              {/* ¿Molestia o dolor esta semana? */}
              <div
                style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: 'var(--surface-sunken)',
                  borderRadius: '8px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: hasDiscomfort ? '0.75rem' : '0' }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 700 }}>
                    ¿Has tenido alguna molestia o dolor físico esta semana?
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => setHasDiscomfort(false)}
                      className={`btn ${!hasDiscomfort ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem' }}
                    >
                      No
                    </button>
                    <button
                      type="button"
                      onClick={() => setHasDiscomfort(true)}
                      className={`btn ${hasDiscomfort ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem' }}
                    >
                      Sí
                    </button>
                  </div>
                </div>

                {hasDiscomfort && (
                  <div>
                    {discomfortAreas.map((area, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: '0.65rem',
                          backgroundColor: 'var(--surface-card)',
                          borderRadius: '6px',
                          border: '1px solid var(--surface-border)',
                          marginBottom: '0.5rem',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>
                            Zona #{idx + 1}
                          </span>
                          {discomfortAreas.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveDiscomfortArea(idx)}
                              style={{ background: 'none', border: 'none', color: 'var(--status-danger-text)', fontSize: '0.72rem', cursor: 'pointer' }}
                            >
                              Eliminar zona
                            </button>
                          )}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <select
                            value={area.zone}
                            onChange={(e) => handleUpdateDiscomfortArea(idx, { zone: e.target.value as BodyZone })}
                            className="input"
                            style={{ fontSize: '0.75rem' }}
                          >
                            {BODY_ZONES_CONFIG.map((z) => (
                              <option key={z.id} value={z.id}>{z.label}</option>
                            ))}
                          </select>
                          <select
                            value={area.side}
                            onChange={(e) => handleUpdateDiscomfortArea(idx, { side: e.target.value as Laterality })}
                            className="input"
                            style={{ fontSize: '0.75rem' }}
                          >
                            <option value="right">Derecho</option>
                            <option value="left">Izquierdo</option>
                            <option value="bilateral">Bilateral</option>
                            <option value="central">Central</option>
                            <option value="na">N/A</option>
                          </select>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.2rem' }}>
                          <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>Dolor máximo (0–10):</span>
                          <span style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '0.82rem' }}>{area.pain_level} / 10</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="10"
                          step="1"
                          value={area.pain_level}
                          onChange={(e) => handleUpdateDiscomfortArea(idx, { pain_level: Number(e.target.value) })}
                          style={{ width: '100%', accentColor: 'var(--brand-red)' }}
                        />

                        {/* Vinculación con episodio previo */}
                        {openEpisodes.length > 0 && (
                          <div style={{ marginTop: '0.4rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.72rem', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={!area.is_new}
                                onChange={(e) => handleUpdateDiscomfortArea(idx, { is_new: !e.target.checked })}
                              />
                              <span>¿Corresponde a un episodio ya existente?</span>
                            </label>
                            {!area.is_new && (
                              <select
                                value={area.episode_id || ''}
                                onChange={(e) => handleUpdateDiscomfortArea(idx, { episode_id: e.target.value })}
                                className="input"
                                style={{ width: '100%', fontSize: '0.72rem', marginTop: '0.25rem' }}
                              >
                                <option value="">Selecciona episodio...</option>
                                {openEpisodes.map((ep) => (
                                  <option key={ep.id} value={ep.id}>
                                    {ep.incident_date} · {ep.body_zone} ({ep.laterality || 'N/A'})
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        )}
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={handleAddDiscomfortArea}
                      className="btn btn-secondary"
                      style={{ fontSize: '0.72rem', padding: '0.2rem 0.5rem', width: '100%', marginBottom: '0.75rem' }}
                    >
                      <Plus size={13} style={{ marginRight: '0.2rem' }} />
                      Añadir otra zona de molestia
                    </button>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.2rem' }}>
                        Efecto en tus entrenamientos esta semana:
                      </label>
                      <select
                        value={activityImpact}
                        onChange={(e) => setActivityImpact(e.target.value as ActivityEffect)}
                        className="input"
                        style={{ width: '100%', fontSize: '0.78rem' }}
                      >
                        <option value="none">Sin efecto (entrené normal)</option>
                        <option value="normal_with_pain">Entrené normal pero con molestias</option>
                        <option value="modified">Actividad modificada / ejercicios adaptados</option>
                        <option value="missed_sessions">Sesiones perdidas (tuve que parar)</option>
                        <option value="did_not_train">No pude entrenar</option>
                      </select>
                    </div>

                    {activityImpact !== 'none' && (
                      <div style={{ marginTop: '0.4rem' }}>
                        <input
                          type="text"
                          placeholder="Explica brevemente cómo adaptaste o redujiste la actividad..."
                          value={impactExplanation}
                          onChange={(e) => setImpactExplanation(e.target.value)}
                          className="input"
                          style={{ width: '100%', fontSize: '0.78rem' }}
                        />
                      </div>
                    )}

                    <div style={{ marginTop: '0.6rem' }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.2rem' }}>
                        Impacto percibido en tu rendimiento (1 = Sin merma, 5 = Merma severa):
                      </label>
                      <select
                        value={performanceImpact}
                        onChange={(e) => setPerformanceImpact(Number(e.target.value))}
                        className="input"
                        style={{ width: '100%', fontSize: '0.78rem' }}
                      >
                        <option value={1}>1 — Sin merma en mi rendimiento habitual</option>
                        <option value={2}>2 — Merma muy leve</option>
                        <option value={3}>3 — Merma moderada</option>
                        <option value={4}>4 — Merma importante</option>
                        <option value={5}>5 — Incapaz de rendir a nivel competitivo</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* ¿Enfermedad? */}
              <div
                style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: 'var(--surface-sunken)',
                  borderRadius: '8px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 700 }}>
                    ¿Has estado enfermo esta semana (gripe, gastroenteritis, etc.)?
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => setHasIllness(false)}
                      className={`btn ${!hasIllness ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem' }}
                    >
                      No
                    </button>
                    <button
                      type="button"
                      onClick={() => setHasIllness(true)}
                      className={`btn ${hasIllness ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem' }}
                    >
                      Sí
                    </button>
                  </div>
                </div>

                {hasIllness && (
                  <div style={{ marginTop: '0.6rem' }}>
                    <input
                      type="text"
                      placeholder="Breve descripción del proceso (ej. proceso gripal, fiebre 38º)"
                      value={illnessDescription}
                      onChange={(e) => setIllnessDescription(e.target.value)}
                      className="input"
                      style={{ width: '100%', fontSize: '0.78rem', marginBottom: '0.4rem' }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        Días de entrenamiento perdidos:
                      </span>
                      <input
                        type="number"
                        min="0"
                        max="7"
                        value={illnessDaysMissed}
                        onChange={(e) => setIllnessDaysMissed(Number(e.target.value))}
                        className="input"
                        style={{ width: '70px', fontSize: '0.78rem' }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* ¿Atención profesional? */}
              <div
                style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: 'var(--surface-sunken)',
                  borderRadius: '8px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 700 }}>
                    ¿Recibiste atención profesional (fisio, médico, podólogo)?
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => setHasProfessionalCare(false)}
                      className={`btn ${!hasProfessionalCare ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem' }}
                    >
                      No
                    </button>
                    <button
                      type="button"
                      onClick={() => setHasProfessionalCare(true)}
                      className={`btn ${hasProfessionalCare ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ fontSize: '0.72rem', padding: '0.25rem 0.6rem' }}
                    >
                      Sí
                    </button>
                  </div>
                </div>

                {hasProfessionalCare && (
                  <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    <input
                      type="text"
                      placeholder="Tipo de profesional (ej. Fisioterapeuta del club)"
                      value={careType}
                      onChange={(e) => setCareType(e.target.value)}
                      className="input"
                      style={{ width: '100%', fontSize: '0.78rem' }}
                    />
                    <input
                      type="text"
                      placeholder="Motivo de la atención (ej. Descarga en gemelos, sobrecarga pélvica)"
                      value={careReason}
                      onChange={(e) => setCareReason(e.target.value)}
                      className="input"
                      style={{ width: '100%', fontSize: '0.78rem' }}
                    />
                    <input
                      type="text"
                      placeholder="Pautas o limitaciones indicadas (ej. no saltar, pauta estirar gemelos)"
                      value={careLimitation}
                      onChange={(e) => setCareLimitation(e.target.value)}
                      className="input"
                      style={{ width: '100%', fontSize: '0.78rem' }}
                    />
                  </div>
                )}
              </div>

              {/* Disponibilidad declarada */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, marginBottom: '0.3rem' }}>
                  Tu autopercepción de disponibilidad para la próxima semana *
                </label>
                <select
                  value={declaredAvailability}
                  onChange={(e) => setDeclaredAvailability(e.target.value as DeclaredAvailability)}
                  className="input"
                  style={{ width: '100%', fontSize: '0.82rem' }}
                >
                  <option value="available_100">100% disponible (entrenamiento completo)</option>
                  <option value="limited">Disponible con limitaciones (adaptar volumen o ejercicios)</option>
                  <option value="needs_assessment">Solicito valoración médica antes de entrenar</option>
                  <option value="unavailable">No disponible para entrenar</option>
                </select>
              </div>

              {/* Solicitud de contacto */}
              <div
                style={{
                  padding: '0.75rem',
                  backgroundColor: contactRequested ? 'rgba(59, 130, 246, 0.08)' : 'var(--surface-sunken)',
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: contactRequested ? 'var(--brand-blue, #3b82f6)' : 'var(--surface-border)',
                }}
              >
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
                  <input
                    type="checkbox"
                    checked={contactRequested}
                    onChange={(e) => setContactRequested(e.target.checked)}
                  />
                  <span>Solicitar contacto con el servicio médico / fisioterapia</span>
                </label>

                {contactRequested && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <input
                      type="text"
                      placeholder="Comentario sobre el motivo de contacto o preferencia horaria..."
                      value={contactComment}
                      onChange={(e) => setContactComment(e.target.value)}
                      className="input"
                      style={{ width: '100%', fontSize: '0.78rem' }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* PIE DEL MODAL: NAVEGACIÓN Y ACCIONES */}
        <div
          className="mobile-form-actions"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '0.75rem',
            borderTop: '1px solid var(--surface-border)',
          }}
        >
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={isSavingDraft || isSubmitting}
            className="btn btn-secondary"
            style={{ fontSize: '0.78rem', minHeight: '44px' }}
          >
            <Save size={14} style={{ marginRight: '0.3rem' }} />
            {isSavingDraft ? 'Guardando...' : 'Guardar borrador online'}
          </button>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {currentStep > 1 && (
              <button
                type="button"
                onClick={() => setCurrentStep(currentStep - 1)}
                className="btn btn-secondary"
                style={{ fontSize: '0.78rem', minHeight: '44px' }}
              >
                <ChevronLeft size={14} style={{ marginRight: '0.2rem' }} />
                Anterior
              </button>
            )}

            {currentStep < 5 ? (
              <button
                type="button"
                onClick={() => setCurrentStep(currentStep + 1)}
                className="btn btn-primary"
                style={{ fontSize: '0.78rem', minHeight: '44px' }}
              >
                Siguiente
                <ChevronRight size={14} style={{ marginLeft: '0.2rem' }} />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleOpenConfirmSubmit}
                disabled={isSubmitting}
                className="btn btn-primary"
                style={{ fontSize: '0.78rem', backgroundColor: 'var(--status-success-text)', minHeight: '44px' }}
              >
                <Send size={14} style={{ marginRight: '0.3rem' }} />
                Enviar respuesta
              </button>
            )}
          </div>
        </div>

        {/* SUBMODAL DE CONFIRMACIÓN DE ENVÍO DEFINITIVO */}
        {confirmSubmitModal && (
          <div
            className="mobile-viewport-overlay"
            role="alertdialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(10, 19, 32, 0.85)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1400,
              padding: '1rem',
            }}
          >
            <div
              style={{
                backgroundColor: 'var(--surface-card)',
                borderRadius: '12px',
                padding: '1.25rem',
                maxWidth: '430px',
                width: '100%',
                boxShadow: 'var(--shadow-card)',
                border: '1px solid var(--surface-border)',
              }}
            >
              <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                ¿Confirmar envío del seguimiento semanal?
              </h3>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '0.75rem' }}>
                Se enviarán tus respuestas correspondientes al periodo <strong>{periodStart} al {periodEnd}</strong>.
                Tu estado cambiará a <strong>Enviado</strong> y se registrará la versión <strong>{isV1 ? 'weekly_v1' : 'weekly_v2'}</strong>.
              </p>

              {/* Resumen de nutrición en confirmación */}
              <div
                style={{
                  backgroundColor: 'var(--surface-sunken)',
                  borderRadius: '6px',
                  padding: '0.6rem 0.75rem',
                  fontSize: '0.75rem',
                  marginBottom: '1rem',
                  lineHeight: 1.4,
                  border: '1px solid var(--surface-border)',
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: '0.35rem', color: 'var(--text-main)' }}>
                  Alimentación e Hidratación:
                </div>
                <div>• Calidad: <strong>{nutritionQuality ? NUTRITION_QUALITY_LABELS[nutritionQuality] : '—'}</strong></div>
                <div>• Agua media: <strong>{waterIntake ? WATER_INTAKE_LABELS[waterIntake] : '—'}</strong></div>
                <div>• Proteína en comidas: <strong>{proteinFrequency ? PROTEIN_FREQUENCY_LABELS[proteinFrequency] : '—'}</strong></div>
                <div>• Fruta y verdura: <strong>{fruitVegServings ? FRUIT_VEG_SERVINGS_LABELS[fruitVegServings] : '—'}</strong></div>
                <div>• Post-entreno: <strong>{postWorkoutIntake ? POST_WORKOUT_INTAKE_LABELS[postWorkoutIntake] : '—'}</strong></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setConfirmSubmitModal(false)}
                  className="btn btn-secondary"
                  disabled={isSubmitting}
                >
                  Revisar respuestas
                </button>
                <button
                  type="button"
                  onClick={handleFinalSubmit}
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Enviando...' : 'Confirmar y Enviar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
